/**
 * Inactive rules context — raw React context and consumer hooks.
 *
 * Mirrors the `ruleContext.ts` split pattern (no JSX here) so Vite's
 * react-refresh plugin stays happy. The provider component lives in
 * `RuleContextProvider.tsx` — it imports `InactiveRulesContext` from
 * here and owns the `<Provider>` element.
 *
 * Default value is an empty map so components wrapped by
 * {@link withComponentRules} keep working on pages that have not yet
 * adopted a `RuleContextProvider`.
 */

import { createContext, useContext, useMemo } from 'react';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * Module-level React context carrying the tenant's inactive-rules map.
 *
 * Key: `componentId`. Value: a `Set<string>` of rule ids the tenant has
 * locally suppressed. Consumers should prefer {@link useInactiveRuleIdsFor}
 * over reading the map directly.
 */
export const InactiveRulesContext = createContext<Record<string, Set<string>>>(
  {},
);

InactiveRulesContext.displayName = 'InactiveRulesContext';

/**
 * Sibling readiness flag for the tenant-wide inactive-rules fetch.
 *
 * `true` means the inactive-rules query has settled (or the consumer is
 * outside any provider and is therefore not waiting on it). The
 * {@link withComponentRules} HOC folds this into its render gate so a
 * tenant-suppressed rule cannot apply for one frame and then be filtered
 * out when the inactive map resolves (a flash in the other direction).
 *
 * Default `true` is deliberate: a component rendered on a page **without**
 * a `RuleContextProvider` has no suppression map to wait on and must not
 * block forever on inactive readiness. It still gates on its own
 * active-rules `isLoading`, so it remains flash-free.
 */
export const InactiveRulesReadyContext = createContext<boolean>(true);

InactiveRulesReadyContext.displayName = 'InactiveRulesReadyContext';

// ---------------------------------------------------------------------------
// Consumer hooks
// ---------------------------------------------------------------------------

/**
 * Read the full inactive-rules map from the closest provider.
 *
 * Returns an empty map when called outside any provider — the hook
 * intentionally does **not** throw so that configurable components can
 * be dropped on pages that have not yet adopted a provider.
 */
export function useInactiveRulesContext(): Record<string, Set<string>> {
  return useContext(InactiveRulesContext);
}

// Shared empty set reused when a component has no inactive ids so
// consumers can safely `.has(...)` without allocating a fresh Set on
// every render.
const EMPTY_SET: Set<string> = new Set();

/**
 * Look up the inactive-rule-id set for a single component.
 *
 * Memoised on the component id and the map reference so the returned
 * set identity is stable across renders when nothing has changed —
 * downstream `useMemo` deps remain effective.
 */
export function useInactiveRuleIdsFor(componentId: string): Set<string> {
  const map = useInactiveRulesContext();
  return useMemo(() => map[componentId] ?? EMPTY_SET, [map, componentId]);
}

/**
 * Read whether the tenant-wide inactive-rules fetch has settled.
 *
 * Returns `true` when called outside any provider — see
 * {@link InactiveRulesReadyContext} for why the default must be ready so a
 * provider-less page never blocks on inactive rules.
 */
export function useInactiveRulesReady(): boolean {
  return useContext(InactiveRulesReadyContext);
}
